/**
 * intl context
 */
import React, { useState } from 'react';
import { DefaultOptionType } from 'antd/lib/select';

import {
  UserIntlStorage,
  IntlBaseConfig,
  getCurrentIntlConfig,
  checkConfigIntegrity,
} from '@/utils/intl';
import {
  getTenantConfigWithCache,
  getTenantSysConfigWithCache,
  getUserConfigWithCache,
} from '@/api/cacheRequest';
import { updateUserIntlConfig, getMobileRegionList } from '@/api/login';
import { getInitialLanguage } from '@/config/locales';
import { IdataSource } from '@/utils';

import {
  TenantGlobalConfig,
  TenantConfigValue,
  setCurrentTimeZone,
  TenantBaseConfig,
  TenantSysConfig,
} from './helper';

interface CtxValue extends TenantConfigValue {
  initTenantConfig: (params: { isUserLogin: boolean }) => void;
  regionList: DefaultOptionType[];
}

const IntlContext = React.createContext<CtxValue>({
  ...(TenantGlobalConfig.getConfigValue() as TenantConfigValue),
  initTenantConfig: () => null,
  regionList: [],
});

export const IntlContextWrapper = (props) => {
  const [baseConfig, setBaseConfig] = useState<TenantBaseConfig>({
    CurrencySymbol: '',
    MobileAreaCode: null,
  });
  const [systemConfig, setSystemConfig] = useState<Partial<TenantSysConfig>>({});
  const [regionList, setRegionList] = useState<DefaultOptionType[]>([]);

  const initTenantConfig: CtxValue['initTenantConfig'] = async (params) => {
    // 配置 用户>本地>租户
    const updateConfig: Partial<IntlBaseConfig> = {
      DSTStatus: false,
    };
    // 初始语言
    const initLanguage = getInitialLanguage();

    try {
      const { isUserLogin } = params;

      const requestQueue = [
        getCurrentIntlConfig(),
        getTenantConfigWithCache().then(
          (res) => res.data,
          () => null,
        ),
        getMobileRegionList().then(
          (res) => res.data,
          () => null,
        ),
      ];
      if (isUserLogin) {
        requestQueue.push(
          getUserConfigWithCache().then(
            (res) => res.data,
            () => null,
          ),
          getTenantSysConfigWithCache().then(
            (res) => res.data,
            () => null,
          ),
        );
      }

      const [
        localConfig,
        tenantConfig,
        regionOptions,
        userConfig,
        tenantSysConfig,
      ] = await Promise.all(requestQueue);

      // 用户是否初始化
      const userInitialized = !!(userConfig && userConfig.language);

      // 手机号区号列表
      setRegionList(
        (regionOptions || []).map((item) => {
          return {
            label: item.name,
            value: item.areaCode,
          };
        }),
      );

      if (tenantConfig) {
        // 租户配置存在
        const tenantBaseConfig: TenantBaseConfig = {
          CurrencySymbol: tenantConfig.currency,
          MobileAreaCode: tenantConfig.defaultAreaCode,
        };
        setBaseConfig(tenantBaseConfig);
        TenantGlobalConfig.setConfigConfig({ TenantBaseConfig: tenantBaseConfig });
        updateConfig.Language = tenantConfig.language;
        updateConfig.TimeZone = tenantConfig.timeZoneId;
        updateConfig.TimeZoneCode = tenantConfig.timeZoneCode;
      }

      if (tenantSysConfig) {
        setSystemConfig(tenantSysConfig);
        const certTypeList = tenantSysConfig.certTypes || [];
        TenantGlobalConfig.setConfigConfig({
          TenantSysConfig: {
            ...tenantSysConfig,
            certTypesDataSource: certTypeList.map(
              (item) =>
                ({
                  label: Object.values(item)?.[0],
                  value: Number(Object.keys(item)?.[0]),
                } as IdataSource),
            ),
            certTypesMap: certTypeList.reduce((acc, cur) => ({ ...acc, ...cur }), {}),
          },
        });
      }

      if (localConfig) {
        // 本地配置存在
        updateConfig.Language = localConfig.Language;
        updateConfig.TimeZone = localConfig.TimeZone;
        updateConfig.TimeZoneCode = localConfig.TimeZoneCode;
        updateConfig.DSTStatus = !!localConfig.DSTStatus;
      }

      if (userInitialized) {
        // 用户配置存在
        updateConfig.Language = userConfig.language;
        updateConfig.TimeZone = userConfig.timeZoneId;
        updateConfig.TimeZoneCode = userConfig.timeZoneCode;
        updateConfig.DSTStatus = !!userConfig.daylightTimeFlag;
      }

      if (isUserLogin && !userInitialized && checkConfigIntegrity(updateConfig)) {
        // 用户配置不存在 自动同步本地配置
        await updateUserIntlConfig({
          language: updateConfig.Language,
          timeZoneId: updateConfig.TimeZone,
          timeZoneCode: updateConfig.TimeZoneCode,
          daylightTimeFlag: updateConfig.DSTStatus,
          scene: 'FE_LOCAL_SYNC',
        });
      }
    } catch (e) {
      //
    }

    if (updateConfig.Language) {
      setCurrentTimeZone(updateConfig.TimeZoneCode, updateConfig.DSTStatus);
      UserIntlStorage.setLocalConfig(updateConfig);
      // 当前语言和用户语言不一致
      if (initLanguage !== updateConfig.Language) {
        window.location.reload();
      }
    }
  };

  return (
    <IntlContext.Provider
      value={{
        TenantBaseConfig: baseConfig,
        TenantSysConfig: systemConfig,
        initTenantConfig,
        regionList,
      }}
    >
      {props.children}
    </IntlContext.Provider>
  );
};

export default IntlContext;
